import React, { useCallback, useEffect, useRef, useState } from 'react';

type Direction = 'horizontal' | 'vertical';

interface Props {
  direction: Direction;
  storageKey: string;
  defaultSize: number;             // initial anchored size in px
  anchor?: 'first' | 'second';     // which child is anchored to defaultSize; default 'first'
  min?: number;
  max?: number;
  onResize?: (size: number) => void;
  children: [React.ReactNode, React.ReactNode];
}

function loadStored(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  } catch { return fallback; }
}

const SplitPane: React.FC<Props> = ({
  direction,
  storageKey,
  defaultSize,
  anchor = 'first',
  min = 80,
  max = 99999,
  onResize,
  children,
}) => {
  const [size, setSize] = useState<number>(() => loadStored(storageKey, defaultSize));
  const containerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef<boolean>(false);
  const persistTimerRef = useRef<number | null>(null);

  // Clamp helper that respects container bounds so we can't drag past the edge.
  const clamp = useCallback((next: number): number => {
    const containerEl = containerRef.current;
    let upper = max;
    if (containerEl) {
      const rect = containerEl.getBoundingClientRect();
      const containerSize = direction === 'horizontal' ? rect.width : rect.height;
      upper = Math.min(max, containerSize - min - 6); // leave room for gutter + second pane
    }
    return Math.max(min, Math.min(upper, next));
  }, [direction, min, max]);

  const persist = useCallback((value: number) => {
    if (persistTimerRef.current !== null) window.clearTimeout(persistTimerRef.current);
    persistTimerRef.current = window.setTimeout(() => {
      try { localStorage.setItem(storageKey, String(value)); } catch { /* ignore */ }
    }, 250);
  }, [storageKey]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    draggingRef.current = true;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    document.body.style.cursor = direction === 'horizontal' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
  }, [direction]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    const containerEl = containerRef.current;
    if (!containerEl) return;
    const rect = containerEl.getBoundingClientRect();
    // Compute the size for whichever child is anchored. When anchored to the
    // second child, measure from the far edge so the bottom/right pane grows
    // as the gutter is dragged toward it.
    let raw: number;
    if (direction === 'horizontal') {
      raw = anchor === 'first' ? e.clientX - rect.left : rect.right - e.clientX;
    } else {
      raw = anchor === 'first' ? e.clientY - rect.top : rect.bottom - e.clientY;
    }
    const next = clamp(raw);
    setSize(next);
    persist(next);
    onResize?.(next);
  }, [direction, anchor, clamp, persist, onResize]);

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    onResize?.(size);
  }, [onResize, size]);

  // Re-clamp on window resize so the split stays valid when the container shrinks.
  useEffect(() => {
    const onWin = () => {
      const clamped = clamp(size);
      if (clamped !== size) {
        setSize(clamped);
        persist(clamped);
        onResize?.(clamped);
      }
    };
    window.addEventListener('resize', onWin);
    return () => window.removeEventListener('resize', onWin);
  }, [size, clamp, persist, onResize]);

  const isHorizontal = direction === 'horizontal';
  // Each child wrapper is itself a flex column so that the SINGLE inner element
  // (e.g. `.ide-body`, `.ide-test-pane`) can use `flex: 1` and fill the wrapper.
  // Without this, the wrapper is a plain block and the inner element collapses
  // to its content height, leaving cream-coloured gaps in the page.
  const wrapBase: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
  };
  const fixedStyle: React.CSSProperties = isHorizontal
    ? { ...wrapBase, width: size, flexShrink: 0 }
    : { ...wrapBase, height: size, flexShrink: 0 };
  const flexStyle: React.CSSProperties = { ...wrapBase, flex: 1 };
  const firstStyle = anchor === 'first' ? fixedStyle : flexStyle;
  const secondStyle = anchor === 'first' ? flexStyle : fixedStyle;

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: isHorizontal ? 'row' : 'column',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <div style={firstStyle}>{children[0]}</div>
      <div
        className={`sk-split-gutter ${isHorizontal ? 'horizontal' : 'vertical'}`}
        role="separator"
        aria-orientation={isHorizontal ? 'vertical' : 'horizontal'}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      />
      <div style={secondStyle}>{children[1]}</div>
    </div>
  );
};

export default SplitPane;
